<template>
  <AppSidebar>
    <div class="space-y-6">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-gray-900 dark:text-white uppercase tracking-wider" style="font-family: Poppins, sans-serif">Solutions Directory</h2>
          <p class="text-gray-600 dark:text-gray-400 text-sm">Manage your services page content, listings, and pricing tiers.</p>
        </div>
      </div>


      <!-- Tabs -->
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div class="border-b border-gray-100 dark:border-gray-700">
          <nav class="flex overflow-x-auto custom-scrollbar">
            <button
              v-for="tab in tabs"
              :key="tab.id"
              @click="activeTab = tab.id"
              class="px-6 py-4 text-xs font-bold uppercase tracking-widest whitespace-nowrap transition-all duration-300 border-b-2"
              :class="[
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400 bg-primary-50/30 dark:bg-primary-900/10'
                  : 'border-transparent text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/30'
              ]"
            >
              {{ tab.name }}
            </button>
          </nav>
        </div>

        <div class="p-6 sm:p-8">
          <!-- Loading State -->
          <div v-if="loading" class="flex justify-center py-12">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            <span class="sr-only">Loading...</span>
          </div>

          <div v-else>
            <!-- Hero Section Tab -->
            <div v-if="activeTab === 'hero'" class="space-y-6">
              <div class="max-w-3xl">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6" style="font-family: Poppins, sans-serif">Hero Section Settings</h3>
                <form @submit.prevent="saveSection('hero')" class="space-y-5">
                  <div>
                    <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Main Heading</label>
                    <input 
                      v-model="localSections.hero.data.title" 
                      type="text" 
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Description</label>
                    <textarea 
                      v-model="localSections.hero.data.subtitle" 
                      rows="3"
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"
                    ></textarea>
                  </div>
                  <div class="flex justify-end pt-4">
                    <button type="submit" class="btn btn-primary px-8 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-black">Commit Changes</button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Intro Section Tab -->
            <div v-if="activeTab === 'intro'" class="space-y-6">
              <div class="max-w-3xl">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6" style="font-family: Poppins, sans-serif">Introduction Section</h3>
                <form @submit.prevent="saveSection('intro')" class="space-y-5">
                  <div>
                    <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Section Title</label>
                    <input 
                      v-model="localSections.intro.data.title" 
                      type="text" 
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                    />
                  </div>
                  <div>
                    <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Content Overview</label>
                    <textarea 
                      v-model="localSections.intro.data.description" 
                      rows="4"
                      class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"
                    ></textarea>
                  </div>
                  <div class="flex justify-end pt-4">
                    <button type="submit" class="btn btn-primary px-8 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-black">Commit Changes</button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Process Section Tab -->
            <div v-if="activeTab === 'process'" class="space-y-6">
              <div class="max-w-3xl">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-6" style="font-family: Poppins, sans-serif">Operational Process</h3>
                <form @submit.prevent="saveSection('process')" class="space-y-8">
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Process Title</label>
                      <input 
                        v-model="localSections.process.data.title" 
                        type="text" 
                        class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm"
                      />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Abstract</label>
                      <textarea 
                        v-model="localSections.process.data.description" 
                        rows="1"
                        class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"
                      ></textarea>
                    </div>
                  </div>

                  <div class="border-t border-gray-100 dark:border-gray-700 pt-8">
                    <div class="flex items-center justify-between mb-6">
                      <h4 class="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Process Steps</h4>
                    </div>
                    <div class="space-y-4">
                      <div 
                        v-for="(step, index) in localSections.process.data.steps" 
                        :key="index"
                        class="bg-gray-50 dark:bg-gray-900/50 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 relative group transition-all hover:bg-white dark:hover:bg-gray-800"
                      >
                        <button 
                          type="button"
                          @click="removeProcessStep(index)"
                          class="absolute top-4 right-4 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon class="w-4 h-4" />
                        </button>
                        <div class="grid grid-cols-1 gap-5">
                          <div>
                            <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Step {{ index + 1 }} Title</label>
                            <input 
                              v-model="step.title" 
                              type="text" 
                              class="w-full bg-transparent border-none focus:ring-0 p-0 text-gray-900 dark:text-white font-bold uppercase tracking-wider text-sm placeholder-gray-300"
                              placeholder="Enter step title"
                            />
                          </div>
                          <div>
                            <label class="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2">Description</label>
                            <textarea 
                              v-model="step.description" 
                              rows="2"
                              class="w-full bg-transparent border-none focus:ring-0 p-0 text-gray-600 dark:text-gray-400 text-xs placeholder-gray-200 resize-none"
                              placeholder="Describe this phase..."
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      <button 
                        type="button"
                        @click="addProcessStep"
                        class="w-full py-6 border-2 border-dashed border-gray-100 dark:border-gray-700 rounded-2xl text-gray-300 dark:text-gray-600 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 font-bold text-[10px] uppercase tracking-widest transition-all group"
                      >
                        <span class="flex items-center justify-center gap-2">
                          <PlusIcon class="w-4 h-4" />
                          Add Process Step
                        </span>
                      </button>
                    </div>
                  </div>

                  <div class="flex justify-end pt-4">
                    <button type="submit" class="btn btn-primary px-8 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-black">Commit Changes</button>
                  </div>
                </form>
              </div>
            </div>

            <!-- Services Management Tab -->
            <div v-if="activeTab === 'services'" class="space-y-6">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest" style="font-family: Poppins, sans-serif">Service Catalog</h3>
                <button @click="openServiceModal()" class="btn btn-primary flex items-center justify-center gap-2 px-6 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-bold">
                  <PlusIcon class="w-4 h-4" />
                  Add New Service
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div 
                  v-for="service in services" 
                  :key="service.id"
                  class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden hover:shadow-xl transition-all group relative"
                >
                  <!-- Banner Image -->
                  <div class="w-full h-40 bg-gray-50 dark:bg-gray-900 relative overflow-hidden">
                    <img 
                      v-if="service.image"
                      :src="getBannerUrl(service.image)" 
                      :alt="service.name"
                      class="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-900">
                      <PhotoIcon class="w-12 h-12 text-gray-200 dark:text-gray-800" />
                    </div>
                    <!-- Status Badge -->
                    <div class="absolute top-4 left-4">
                      <span 
                        class="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border shadow-sm"
                        :class="service.status === 'active' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-gray-500/10 text-gray-400 border-gray-500/20'"
                      >
                        {{ service.status }}
                      </span>
                    </div>
                  </div>

                  <div class="p-6">
                    <div class="flex justify-between items-start mb-4">
                      <div class="flex-1">
                        <div class="flex flex-wrap gap-1.5 mb-2">
                          <template v-if="service.categories && service.categories.length > 0">
                            <span v-for="cat in service.categories" :key="cat.id" class="px-2 py-0.5 text-[8px] font-bold uppercase tracking-widest rounded bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-primary-800">
                              {{ cat.name }}
                            </span>
                          </template>
                        </div>
                        <h4 class="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-wider line-clamp-1">{{ service.name }}</h4>
                      </div>
                      <div class="flex gap-2 ml-4">
                        <button 
                          @click="openServiceModal(service)"
                          class="p-2 text-gray-300 hover:text-primary-500 transition-colors"
                        >
                          <PencilIcon class="w-4 h-4" />
                        </button>
                        <button 
                          @click="deleteService(service.id)"
                          class="p-2 text-gray-300 hover:text-red-500 transition-colors"
                        >
                          <TrashIcon class="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <p class="text-gray-500 dark:text-gray-400 text-xs mb-6 line-clamp-2 h-8 leading-relaxed">{{ service.short_description }}</p>
                    
                    <div class="flex items-center justify-between pt-5 border-t border-gray-50 dark:border-gray-700/50">
                      <span class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Starting At</span>
                      <span class="text-xl font-bold text-primary-600 dark:text-primary-400 italic tracking-tighter">${{ service.starting_price }}<span class="text-[10px] ml-0.5 uppercase tracking-widest">+</span></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Service Categories Tab -->
            <div v-if="activeTab === 'categories'" class="space-y-6">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest" style="font-family: Poppins, sans-serif">Service Classifications</h3>
                <button @click="openCategoryModal()" class="btn btn-primary flex items-center justify-center gap-2 px-6 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-bold">
                  <PlusIcon class="w-4 h-4" />
                  Add Category
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="cat in categories" :key="cat.id" class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 hover:shadow-xl transition-all group relative">
                  <div class="absolute -top-4 -right-4 w-12 h-12 bg-primary-500/5 group-hover:bg-primary-500/10 rounded-full blur-xl transition-all"></div>
                  
                  <div class="flex justify-between items-start mb-4">
                    <div class="w-10 h-10 bg-primary-50 dark:bg-primary-900/10 rounded-xl flex items-center justify-center text-lg shadow-inner">
                      {{ cat.icon || '📁' }}
                    </div>
                    <div class="flex gap-2">
                      <button @click="openCategoryModal(cat)" class="p-2 text-gray-300 hover:text-primary-500 transition-colors">
                        <PencilIcon class="w-4 h-4" />
                      </button>
                      <button @click="deleteCategory(cat.id)" class="p-2 text-gray-300 hover:text-red-500 transition-colors">
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <h4 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-widest mb-2">{{ cat.name }}</h4>
                  <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2" v-if="cat.description">{{ cat.description }}</p>
                </div>
              </div>
            </div>

            <!-- Service Packages Tab -->
            <div v-if="activeTab === 'packages'" class="space-y-6">
              <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                <h3 class="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest" style="font-family: Poppins, sans-serif">Service Tiers</h3>
                <button @click="openPackageModal()" class="btn btn-primary flex items-center justify-center gap-2 px-6 shadow-lg shadow-primary-500/20 uppercase tracking-widest text-[10px] font-bold">
                  <PlusIcon class="w-4 h-4" />
                  Add Package
                </button>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div v-for="pkg in packages" :key="pkg.id" class="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-2xl p-6 hover:shadow-xl transition-all group relative overflow-hidden">
                  <div class="absolute top-0 right-0 w-32 h-32 bg-primary-500/5 rotate-45 translate-x-16 -translate-y-16 group-hover:bg-primary-500/10 transition-colors"></div>
                  
                  <div class="flex justify-between items-start mb-6">
                    <div>
                      <h4 class="text-lg font-bold text-gray-900 dark:text-white uppercase tracking-wider">{{ pkg.name }}</h4>
                      <p class="text-[10px] font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mt-1" v-if="pkg.service">{{ pkg.service.name }}</p>
                    </div>
                    <div class="flex gap-2">
                      <button @click="openPackageModal(pkg)" class="p-2 text-gray-300 hover:text-primary-500 transition-colors">
                        <PencilIcon class="w-4 h-4" />
                      </button>
                      <button @click="deletePackage(pkg.id)" class="p-2 text-gray-300 hover:text-red-500 transition-colors">
                        <TrashIcon class="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <p class="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-6 line-clamp-2 h-8" v-if="pkg.description">{{ pkg.description }}</p>
                  <div class="flex items-center justify-between pt-5 border-t border-gray-50 dark:border-gray-700/50">
                    <span class="text-2xl font-bold text-gray-900 dark:text-white italic tracking-tighter">${{ pkg.price }}</span>
                    <span class="px-3 py-1 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest" v-if="pkg.timeline_weeks">{{ pkg.timeline_weeks }} Weeks</span>
                  </div>
                </div>
              </div>
            </div>


      <!-- Service Modal -->
      <div v-if="showServiceModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-900/90 backdrop-blur-sm transition-opacity" @click="closeServiceModal"></div>

          <div class="relative bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full max-h-[90vh] flex flex-col border border-gray-100 dark:border-gray-700">
            <div class="p-6 sm:p-8 overflow-y-auto flex-grow custom-scrollbar">
              <div class="w-full">
                <h3 class="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-8" id="modal-title" style="font-family: Poppins, sans-serif">
                  {{ editingService ? 'Edit Service Details' : 'Create New Service' }}
                </h3>
                  <div class="mt-6 space-y-6">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div class="col-span-full">
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Service Name</label>
                        <input v-model="serviceForm.name" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm font-medium" required />
                      </div>

                      <div class="col-span-full">
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Assign Categories</label>
                        <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700 max-h-32 overflow-y-auto custom-scrollbar">
                          <label v-for="cat in categories" :key="cat.id" class="flex items-center space-x-3 text-xs cursor-pointer group">
                            <input type="checkbox" :value="cat.id" v-model="serviceForm.category_ids" class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-white dark:bg-gray-800" />
                            <span class="text-gray-600 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors uppercase tracking-widest font-bold text-[9px]">{{ cat.name }}</span>
                          </label>
                        </div>
                      </div>

                      <div class="col-span-full">
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Available Packages</label>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700 max-h-40 overflow-y-auto custom-scrollbar">
                          <label v-for="pkg in packages" :key="pkg.id" class="flex items-center space-x-3 text-xs cursor-pointer group bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-primary-500/50 transition-all">
                            <input type="checkbox" :value="pkg.id" v-model="serviceForm.package_ids" class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent" />
                            <div class="flex flex-col">
                              <span class="text-gray-900 dark:text-white font-bold uppercase tracking-wider text-[10px]">{{ pkg.name }}</span>
                              <span class="text-primary-600 dark:text-primary-400 text-[10px] font-bold italic tracking-tighter">${{ pkg.price }}</span>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Short Summary</label>
                      <textarea v-model="serviceForm.short_description" rows="2" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none font-medium" required></textarea>
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Full Description</label>
                      <textarea v-model="serviceForm.long_description" rows="4" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none font-medium leading-relaxed" required></textarea>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-3 gap-5">
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Starting Price ($)</label>
                        <input v-model="serviceForm.starting_price" type="number" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Min Weeks</label>
                        <input v-model="serviceForm.timeline_min_weeks" type="number" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Max Weeks</label>
                        <input v-model="serviceForm.timeline_max_weeks" type="number" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Service Features (One per line)</label>
                      <textarea v-model="serviceFormFeatures" rows="3" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none font-medium" placeholder="Asset 01&#10;Asset 02"></textarea>
                    </div>

                    <!-- Banner Image Upload Section -->
                    <div class="border-t border-gray-100 dark:border-gray-700 pt-6">
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-4">Service Banner</label>
                      
                      <!-- Current Banner Preview -->
                      <div v-if="editingService && editingService.image" class="mb-5">
                        <div class="relative inline-block group">
                          <img 
                            :src="getBannerUrl(editingService.image)" 
                            :alt="editingService.name"
                            class="w-full max-w-md h-32 object-cover rounded-2xl border border-gray-200 dark:border-gray-700"
                          />
                          <button
                            type="button"
                            @click="handleDeleteBanner"
                            class="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all opacity-0 group-hover:opacity-100 shadow-lg"
                            title="Remove banner"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <!-- Image Preview (for new upload) -->
                      <div v-if="bannerPreview" class="mb-5">
                        <div class="relative inline-block group">
                          <img 
                            :src="bannerPreview" 
                            alt="Banner preview"
                            class="w-full max-w-md h-32 object-cover rounded-2xl border border-primary-500 dark:border-primary-400 shadow-lg"
                          />
                          <button
                            type="button"
                            @click="clearBannerPreview"
                            class="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-all shadow-lg"
                            title="Clear preview"
                          >
                            <TrashIcon class="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <!-- Upload Button -->
                      <div class="flex flex-wrap items-center gap-4">
                        <input
                          ref="bannerInput"
                          type="file"
                          accept="image/jpeg,image/png,image/jpg,image/webp"
                          @change="handleBannerSelect"
                          class="hidden"
                        />
                        <button
                          type="button"
                          @click="$refs.bannerInput.click()"
                          class="px-5 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl text-[10px] font-bold uppercase tracking-widest text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex items-center gap-2"
                        >
                          <PhotoIcon class="w-4 h-4" />
                          {{ editingService?.image ? 'Change Image' : 'Select Image' }}
                        </button>
                        <button
                          v-if="selectedBannerFile && editingService"
                          type="button"
                          @click="handleUploadBanner"
                          :disabled="uploadingBanner"
                          class="px-5 py-2.5 bg-primary-600 text-white rounded-xl text-[10px] font-bold uppercase tracking-widest hover:bg-primary-700 transition-all disabled:opacity-50 shadow-lg shadow-primary-500/20"
                        >
                          {{ uploadingBanner ? 'Uploading...' : 'Upload Now' }}
                        </button>
                      </div>
                      <p class="mt-3 text-[9px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Recommended: 16:9 Aspect • Max 5MB • JPG/PNG/WebP</p>
                    </div>
                    <div class="flex flex-wrap items-center gap-6 pt-6 border-t border-gray-100 dark:border-gray-700">
                      <label class="flex items-center cursor-pointer group">
                        <input v-model="serviceForm.featured" type="checkbox" class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent" />
                        <span class="ml-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest group-hover:text-primary-600 transition-colors">Featured Service</span>
                      </label>
                      <label class="flex items-center cursor-pointer group">
                        <input v-model="serviceForm.status" type="checkbox" true-value="active" false-value="inactive" class="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent" />
                        <span class="ml-3 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest group-hover:text-primary-600 transition-colors">Publicly Active</span>
                      </label>
                    </div>
                  </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-900 px-6 py-5 sm:px-8 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-100 dark:border-gray-700">
              <button 
                type="button" 
                @click="saveService"
                class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg shadow-primary-500/20 px-6 py-2.5 bg-primary-600 text-[10px] font-bold uppercase tracking-widest text-white hover:bg-primary-700 sm:w-auto transition-all"
              >
                {{ editingService ? 'Save Service' : 'Create Service' }}
              </button>
              <button 
                type="button" 
                @click="closeServiceModal"
                class="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm px-6 py-2.5 bg-white dark:bg-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Category Modal -->
      <div v-if="showCategoryModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="category-modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-900/90 backdrop-blur-sm transition-opacity" @click="closeCategoryModal"></div>

          <div class="relative bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full max-h-[90vh] flex flex-col border border-gray-100 dark:border-gray-700">
            <div class="p-6 sm:p-8 overflow-y-auto flex-grow custom-scrollbar">
              <div class="w-full">
                <h3 class="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-8" id="category-modal-title" style="font-family: Poppins, sans-serif">
                  {{ editingCategory ? 'Edit Category' : 'Create New Category' }}
                </h3>
                  <div class="mt-6 space-y-5">
                    <div>
                      <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Label</label>
                      <input v-model="categoryForm.name" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Abstract</label>
                      <textarea v-model="categoryForm.description" rows="3" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"></textarea>
                    </div>
                    <div class="grid grid-cols-2 gap-5">
                      <div>
                        <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Visual Glyph</label>
                        <input v-model="categoryForm.icon" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="e.g., 🚀" />
                      </div>
                      <div>
                        <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Priority Index</label>
                        <input v-model="categoryForm.sort_order" type="number" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" />
                      </div>
                    </div>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-900 px-6 py-5 sm:px-8 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-100 dark:border-gray-700">
              <button 
                type="button" 
                @click="saveCategory"
                class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg shadow-primary-500/20 px-6 py-2.5 bg-primary-600 text-[10px] font-black uppercase tracking-widest text-white hover:bg-primary-700 sm:w-auto transition-all"
              >
                {{ editingCategory ? 'Save Category' : 'Create Category' }}
              </button>
              <button 
                type="button" 
                @click="closeCategoryModal"
                class="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm px-6 py-2.5 bg-white dark:bg-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Package Modal -->
      <div v-if="showPackageModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="package-modal-title" role="dialog" aria-modal="true">
        <div class="flex items-center justify-center min-h-screen p-4 text-center sm:p-0">
          <div class="fixed inset-0 bg-gray-900/90 backdrop-blur-sm transition-opacity" @click="closePackageModal"></div>

          <div class="relative bg-white dark:bg-gray-800 rounded-2xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full max-h-[90vh] flex flex-col border border-gray-100 dark:border-gray-700">
            <div class="p-6 sm:p-8 overflow-y-auto flex-grow custom-scrollbar">
              <div class="w-full">
                <h3 class="text-xs font-bold text-primary-600 dark:text-primary-400 uppercase tracking-widest mb-8" id="package-modal-title" style="font-family: Poppins, sans-serif">
                  {{ editingPackage ? 'Edit Package' : 'Create New Package' }}
                </h3>
                  <div class="mt-6 space-y-6">
                    <div class="col-span-full">
                      <label class="block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">Assign to Services</label>
                      <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700 max-h-40 overflow-y-auto custom-scrollbar">
                        <label v-for="service in services" :key="service.id" class="flex items-center space-x-3 text-xs cursor-pointer group bg-white dark:bg-gray-800 p-2.5 rounded-xl border border-gray-100 dark:border-gray-700 hover:border-primary-500/50 transition-all">
                          <input type="checkbox" :value="service.id" v-model="packageForm.service_ids" class="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500 bg-transparent" />
                          <span class="text-gray-900 dark:text-white font-black uppercase tracking-wider text-[9px]">{{ service.name }}</span>
                        </label>
                      </div>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Package Label</label>
                        <input v-model="packageForm.name" type="text" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                      <div>
                        <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Valuation ($)</label>
                        <input v-model.number="packageForm.price" type="number" step="0.01" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" required />
                      </div>
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Abstract</label>
                      <textarea v-model="packageForm.description" rows="3" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none"></textarea>
                    </div>
                    <div>
                      <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Tier Assets (LF separated)</label>
                      <textarea v-model="packageFormFeatures" rows="4" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm resize-none" placeholder="Asset 01&#10;Asset 02"></textarea>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Timeline (Weeks)</label>
                        <input v-model.number="packageForm.timeline_weeks" type="number" min="1" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" placeholder="e.g., 4" required />
                      </div>
                      <div>
                        <label class="block text-[10px] font-black text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-1.5">Priority Index</label>
                        <input v-model.number="packageForm.sort_order" type="number" class="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all text-sm" />
                      </div>
                </div>
              </div>
            </div>

            <div class="bg-gray-50 dark:bg-gray-900 px-6 py-5 sm:px-8 sm:flex sm:flex-row-reverse gap-3 border-t border-gray-100 dark:border-gray-700">
              <button 
                type="button" 
                @click="savePackage"
                class="w-full inline-flex justify-center rounded-xl border border-transparent shadow-lg shadow-primary-500/20 px-6 py-2.5 bg-primary-600 text-[10px] font-black uppercase tracking-widest text-white hover:bg-primary-700 sm:w-auto transition-all"
              >
                {{ editingPackage ? 'Save Package' : 'Create Package' }}
              </button>
              <button 
                type="button" 
                @click="closePackageModal"
                class="mt-3 w-full inline-flex justify-center rounded-xl border border-gray-200 dark:border-gray-600 shadow-sm px-6 py-2.5 bg-white dark:bg-gray-800 text-[10px] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 sm:mt-0 sm:w-auto transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
          </div>
      </div>
    </div>
    </div>
    </div>

  </AppSidebar>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useContentStore } from '@/stores/content'
import { useServiceStore } from '@/stores/services'
import { PlusIcon, TrashIcon, PencilIcon, PhotoIcon } from '@heroicons/vue/24/outline'
import AppSidebar from '@/components/layout/AppSidebar.vue'

const contentStore = useContentStore()
const serviceStore = useServiceStore()

const loading = ref(true)
const activeTab = ref('hero')
const showServiceModal = ref(false)
const editingService = ref(null)

// Form state for services
const serviceForm = ref({
  name: '',
  category_ids: [],
  package_ids: [],
  short_description: '',
  long_description: '',
  starting_price: 0,
  timeline_min_weeks: 1,
  timeline_max_weeks: 4,
  features: [],
  technologies: [],
  featured: false,
  status: 'active',
  icon: ''
})

const serviceFormFeatures = computed({
  get: () => serviceForm.value.features ? serviceForm.value.features.join('\n') : '',
  set: (val) => {
    serviceForm.value.features = val.split('\n').filter(line => line.trim() !== '')
  }
})

// Banner image upload state
const selectedBannerFile = ref(null)
const bannerPreview = ref(null)
const uploadingBanner = ref(false)
const bannerInput = ref(null)

// Banner image methods
const handleBannerSelect = (event) => {
  const file = event.target.files[0]
  if (!file) return

  // Validate file size (5MB max)
  if (file.size > 5 * 1024 * 1024) {
    alert('File size must be less than 5MB')
    return
  }

  // Validate file type
  if (!['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type)) {
    alert('Only JPG, PNG, and WebP images are allowed')
    return
  }

  selectedBannerFile.value = file

  // Create preview
  const reader = new FileReader()
  reader.onload = (e) => {
    bannerPreview.value = e.target.result
  }
  reader.readAsDataURL(file)
}

const handleUploadBanner = async () => {
  if (!selectedBannerFile.value || !editingService.value) return

  try {
    uploadingBanner.value = true
    const result = await serviceStore.uploadServiceBanner(editingService.value.id, selectedBannerFile.value)
    
    // Update the editingService with the new image path
    if (result && result.image) {
      editingService.value.image = result.image
    }
    
    // Refresh services to get updated data
    await serviceStore.fetchAdminServices()
    
    // Clear preview and file
    clearBannerPreview()
  } catch (error) {
    console.error('Error uploading banner:', error)
  } finally {
    uploadingBanner.value = false
  }
}

const handleDeleteBanner = async () => {
  if (!editingService.value || !confirm('Are you sure you want to delete this banner image?')) return

  try {
    await serviceStore.deleteServiceBanner(editingService.value.id)
    
    // Update the editingService to remove the image
    editingService.value.image = null
    
    // Refresh services
    await serviceStore.fetchAdminServices()
  } catch (error) {
    console.error('Error deleting banner:', error)
  }
}

const clearBannerPreview = () => {
  selectedBannerFile.value = null
  bannerPreview.value = null
  if (bannerInput.value) {
    bannerInput.value.value = ''
  }
}

 

// Category modal state
const showCategoryModal = ref(false)
const editingCategory = ref(null)
const categoryForm = ref({
  name: '',
  description: '',
  icon: '',
  sort_order: 0
})

const tabs = [
  { id: 'hero', name: 'Hero Section' },
  { id: 'intro', name: 'Intro Section' },
  { id: 'process', name: 'Process Section' },
  { id: 'services', name: 'Services Management' },
  { id: 'categories', name: 'Service Categories' },
  { id: 'packages', name: 'Service Packages' }
]

// Data loading
onMounted(async () => {
  loading.value = true
  try {
    await Promise.all([
      contentStore.fetchPageSections('services'),
      serviceStore.fetchAdminServices(),
      serviceStore.fetchCategories(),
      serviceStore.fetchPackages()
    ])
    
    // Data is loaded and synced via watch(rawSections)

  } catch (error) {
    console.error('Error loading data:', error)
  } finally {
    loading.value = false
  }
})

// Local state for sections to handle reactivity correctly
const localSections = ref({
  hero: { section: 'hero', data: { title: '', subtitle: '' } },
  intro: { section: 'intro', data: { title: '', description: '' } },
  process: { section: 'process', data: { title: '', description: '', steps: [] } }
})

// Computed
const rawSections = computed(() => contentStore.pageSections.services || [])

// Watch for rawSections changes to update local state
watch(rawSections, (newSections) => {
  if (newSections && newSections.length > 0) {
    newSections.forEach(s => {
      // Ensure data and steps exist
      const sectionData = JSON.parse(JSON.stringify(s))
      if (s.section === 'process' && !sectionData.data.steps) {
        sectionData.data.steps = []
      }
      localSections.value[s.section] = sectionData
    })
  }
}, { immediate: true, deep: true })

const services = computed(() => serviceStore.services)
const categories = computed(() => serviceStore.categories)
const packages = computed(() => serviceStore.packages)

// Methods
const saveSection = async (sectionName) => {
  const section = localSections.value[sectionName]
  try {
    if (section.id) {
      await contentStore.updatePageSection(section.id, {
        page: 'services',
        section: sectionName,
        data: section.data
      })
    } else {
      await contentStore.createPageSection({
        page: 'services',
        section: sectionName,
        data: section.data
      })
    }
    // Refresh
    await contentStore.fetchPageSections('services')
  } catch (error) {
    console.error('Error saving section:', error)
  }
}

const addProcessStep = () => {
  if (!localSections.value.process.data.steps) {
    localSections.value.process.data.steps = []
  }
  localSections.value.process.data.steps.push({ title: '', description: '' })
}

const removeProcessStep = (index) => {
  localSections.value.process.data.steps.splice(index, 1)
}

// Service CRUD
const openServiceModal = (service = null) => {
  editingService.value = service
  if (service) {
    const formData = JSON.parse(JSON.stringify(service))
    // Map categories to category_ids array
    formData.category_ids = service.categories ? service.categories.map(c => c.id) : (service.category_id ? [service.category_id] : [])
    // Map packages to package_ids array
    formData.package_ids = service.packages ? service.packages.map(p => p.id) : []
    serviceForm.value = formData
  } else {
    serviceForm.value = {
      name: '',
      category_ids: [],
      package_ids: [],
      short_description: '',
      long_description: '',
      starting_price: 0,
      timeline_min_weeks: 1,
      timeline_max_weeks: 4,
      features: [],
      technologies: [],
      featured: false,
      status: 'active',
      icon: ''
    }
  }
  showServiceModal.value = true
}

const closeServiceModal = () => {
  showServiceModal.value = false
  editingService.value = null
  clearBannerPreview()
}

const saveService = async () => {
  try {
    const { image, categories, packages, category_id, ...data } = serviceForm.value
    if (editingService.value) {
      await serviceStore.updateService(editingService.value.id, data)
    } else {
      await serviceStore.addService(data)
    }
    closeServiceModal()
  } catch (error) {
    console.error('Error saving service:', error)
  }
}

const deleteService = async (id) => {
  if (confirm('Are you sure you want to delete this service?')) {
    await serviceStore.deleteService(id)
  }
}

// Category CRUD
const openCategoryModal = (cat = null) => {
  if (cat) {
    editingCategory.value = cat
    categoryForm.value = { ...cat }
  } else {
    editingCategory.value = null
    categoryForm.value = { name: '', description: '', icon: '', sort_order: 0 }
  }
  showCategoryModal.value = true
}

const closeCategoryModal = () => {
  showCategoryModal.value = false
}

const saveCategory = async () => {
  if (editingCategory.value) {
    await serviceStore.updateCategory(editingCategory.value.id, categoryForm.value)
  } else {
    await serviceStore.addCategory(categoryForm.value)
  }
  closeCategoryModal()
  await serviceStore.fetchCategories()
}

const deleteCategory = async (id) => {
  if (confirm('Are you sure you want to delete this category?')) {
    await serviceStore.deleteCategory(id)
  }
}

// Package modal state
const showPackageModal = ref(false)
const editingPackage = ref(null)
const packageForm = ref({
  service_ids: [],
  name: '',
  description: '',
  price: 0,
  features: [],
  timeline_weeks: 1,
  sort_order: 0
})

const packageFormFeatures = computed({
  get: () => packageForm.value.features ? packageForm.value.features.join('\n') : '',
  set: (val) => {
    packageForm.value.features = val.split('\n').filter(line => line.trim() !== '')
  }
})

// Package CRUD
const openPackageModal = (pkg = null) => {
  if (pkg) {
    editingPackage.value = pkg
    const formData = JSON.parse(JSON.stringify(pkg))
    formData.service_ids = pkg.services ? pkg.services.map(s => s.id) : (pkg.service_id ? [pkg.service_id] : [])
    formData.timeline_weeks = pkg.timeline_weeks || 1
    packageForm.value = formData
  } else {
    editingPackage.value = null
    packageForm.value = {
      service_ids: [],
      name: '',
      description: '',
      price: 0,
      features: [],
      timeline_weeks: 1,
      sort_order: 0
    }
  }
  showPackageModal.value = true
}

const closePackageModal = () => {
  showPackageModal.value = false
  editingPackage.value = null
}

const savePackage = async () => {
  try {
    if (!packageForm.value.service_ids || packageForm.value.service_ids.length === 0) {
      alert('Please select at least one service for this package')
      return
    }

    const { services: linkedServices, service_id, ...data } = packageForm.value
    if (editingPackage.value) {
      await serviceStore.updatePackage(editingPackage.value.id, data)
    } else {
      await serviceStore.addPackage(data)
    }
    closePackageModal()
    await serviceStore.fetchPackages()
  } catch (error) {
    console.error('Error saving package:', error)
  }
}

const deletePackage = async (id) => {
  if (confirm('Are you sure you want to delete this package?')) {
    await serviceStore.deletePackage(id)
    await serviceStore.fetchPackages()
  }
}

const getBannerUrl = (imagePath) => {
  return serviceStore.getBannerUrl(imagePath)
}

const getIconUrl = (iconPath) => {
  return serviceStore.getIconUrl(iconPath)
}




</script>
